import { boundaryRings } from "./fieldBoundaryGeometry.js";

export const BOUNDARY_PREVIEW_WIDTH = 184;
export const BOUNDARY_PREVIEW_HEIGHT = 112;
const PADDING = 12;

// A display-only local projection. Saved geometry remains untouched; this is
// neither a geometry validator nor an input to acreage or soil calculations.
export function buildBoundaryPreview(boundaries) {
  if (!Array.isArray(boundaries) || boundaries.length < 3 || boundaries.length > 10000) return null;
  if (boundaries.some((point) => !point ||
    !Number.isFinite(point.lat) || Math.abs(point.lat) > 90 ||
    !Number.isFinite(point.lng) || Math.abs(point.lng) > 180 ||
    !Number.isInteger(point.polygon_index ?? 0) || (point.polygon_index ?? 0) < 0 ||
    !Number.isInteger(point.ring_index ?? 0) || (point.ring_index ?? 0) < 0)) return null;

  const rings = boundaryRings(boundaries);
  const pieces = new Map(rings.filter(({ ring }) => ring === 0).map(({ polygon }) => [polygon, []]));
  const minLat = Math.min(...boundaries.map(({ lat }) => lat));
  const maxLat = Math.max(...boundaries.map(({ lat }) => lat));
  const originLat = (minLat + maxLat) / 2;
  const originLng = boundaries[0].lng;
  const longitudeScale = Math.max(Math.cos(originLat * Math.PI / 180), 0.000001);
  const project = ({ lat, lng }) => ({
    // Unwrap around the field so a date-line crossing stays local.
    x: (((lng - originLng + 540) % 360) - 180) * longitudeScale,
    y: originLat - lat,
  });

  for (const { polygon, ring, points } of rings) {
    if (!pieces.has(polygon) || new Set(points.map(({ lat, lng }) => `${lat}:${lng}`)).size < 3) return null;
    const projected = points.map(project);
    const area = projected.reduce((sum, point, index) => {
      const next = projected[(index + 1) % projected.length];
      return sum + point.x * next.y - next.x * point.y;
    }, 0);
    if (!Number.isFinite(area) || area === 0) return null;
    pieces.get(polygon).push({ ring, points: projected });
  }

  const vertices = [...pieces.values()].flatMap((piece) => piece.flatMap(({ points }) => points));
  const minX = Math.min(...vertices.map(({ x }) => x));
  const maxX = Math.max(...vertices.map(({ x }) => x));
  const minY = Math.min(...vertices.map(({ y }) => y));
  const maxY = Math.max(...vertices.map(({ y }) => y));
  const scale = Math.min(
    (BOUNDARY_PREVIEW_WIDTH - PADDING * 2) / (maxX - minX),
    (BOUNDARY_PREVIEW_HEIGHT - PADDING * 2) / (maxY - minY)
  );
  if (!Number.isFinite(scale)) return null;
  const xOffset = (BOUNDARY_PREVIEW_WIDTH - (maxX - minX) * scale) / 2;
  const yOffset = (BOUNDARY_PREVIEW_HEIGHT - (maxY - minY) * scale) / 2;
  const pointText = ({ x, y }) => `${((x - minX) * scale + xOffset).toFixed(2)},${((y - minY) * scale + yOffset).toFixed(2)}`;

  return {
    pieceCount: pieces.size,
    exclusionCount: rings.length - pieces.size,
    paths: [...pieces.entries()].map(([polygon, piece]) => ({
      polygon,
      // Each piece has its own path; evenodd leaves its exclusions transparent
      // irrespective of the winding stored by the importer or editor.
      d: piece.map(({ points }) => `M${points.map(pointText).join(" L")} Z`).join(" "),
    })),
  };
}
